"use client";

import * as React from "react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/* ──────────────────────────────────────────────────────────────────────────
 * InlineTooltip — inline-text trigger for /learn page explainer asides.
 *
 * Visually distinct from the NKHelpTip pattern (the `?` glyph next to a
 * label). This is a number or short phrase set inline in body prose, with
 * a subtle blue underline + color shift to signal interactivity. On hover
 * (desktop) or tap (mobile), a popover reveals the explainer copy.
 *
 * Used sparingly — currently just the $2,200 tax-refund figure in Act 2,
 * pointing at the tracker's role in keeping the CRA paper trail.
 * ────────────────────────────────────────────────────────────────────────── */

export interface InlineTooltipProps {
  /** The inline text/number rendered as the trigger. */
  children: React.ReactNode;
  /** Accessible label for the trigger button. */
  ariaLabel: string;
  /** Popover body. Plain string or rich content. */
  body: React.ReactNode;
}

export function InlineTooltip({ children, ariaLabel, body }: InlineTooltipProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className="cursor-pointer rounded-sm text-blue-700 underline decoration-blue-300 decoration-dotted underline-offset-[3px] transition-colors hover:text-blue-800 hover:decoration-blue-500 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none"
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        side="top"
        className="w-72 text-sm leading-relaxed text-zinc-700"
      >
        {body}
      </PopoverContent>
    </Popover>
  );
}
